import React, { useState, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { Copy, Check, User, Bot, AlertTriangle, ChevronDown, ChevronRight, ExternalLink, PlayCircle, CalendarClock, Globe } from 'lucide-react';
import { Message, MultiClusterInfo } from '@/types';
import { useAppStore } from '@/stores/appStore';
import { CodeBlock } from './CodeBlock';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ToolTimeline } from '@/components/common/ToolTimeline';
import { toolCallName } from '@/utils/toolCalls';
import { PaginationControls } from './PaginationControls';
import { AuthImage } from './AuthImage';
import { DynamicTable } from './DynamicTable';
import { AlertRuleProposalCard } from './AlertRuleProposalCard';
import { IncidentCardActions } from './IncidentCardActions';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { copyToClipboard } from '@/utils/clipboard';
import { withoutPreviews } from '@/utils/tablePreview';
import { drawableCharts } from '@/utils/chartPayload';
import { providerDisplayName } from '@/utils/answeredBy';
import { InteractiveChart } from './InteractiveChart';
import { toast } from '@/stores/toastStore';

/**
 * Check if tool calls contain validation errors that need retry.
 * Returns true if ALL tool calls failed with validation errors.
 */
function hasValidationErrors(toolCalls?: any[]): boolean {
  if (!toolCalls || toolCalls.length === 0) {
    return false;
  }
  
  // Check if any tool call has a validation error in its output
  const validationErrorIndicators = [
    'VALIDATION ERROR',
    'RETRY IMMEDIATELY',
    'YOU MUST CALL',
    'mutually exclusive',
    'cannot be used together',
  ];
  
  const hasErrors = toolCalls.some(tc => {
    const output = tc.output || tc.result || '';
    const outputStr = typeof output === 'string' ? output : JSON.stringify(output);
    return validationErrorIndicators.some(indicator => 
      outputStr.toUpperCase().includes(indicator.toUpperCase())
    );
  });
  
  return hasErrors;
}

/**
 * Extract the corrected call suggestion from a validation error.
 * Returns the suggested tool call if found, or null.
 */
function extractValidationSuggestion(toolCalls?: any[]): string | null {
  if (!toolCalls || toolCalls.length === 0) {
    return null;
  }
  
  for (const tc of toolCalls) {
    const output = tc.output || tc.result || '';
    const outputStr = typeof output === 'string' ? output : JSON.stringify(output);
    
    // Look for suggested corrected calls
    const patterns = [
      /Option [AB].*?:\s*(v2\w+\([^)]+\))/i,
      /RETRY IMMEDIATELY with.*?:\s*(v2\w+\([^)]+\))/i,
      /corrected call:\s*(v2\w+\([^)]+\))/i,
    ];
    
    for (const pattern of patterns) {
      const match = outputStr.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
  }
  
  return null;
}

/**
 * Detect if a message is a "plan" response - explaining what to do instead of doing it.
 * Returns true if the message looks like an explanation rather than actual execution,
 * OR if the tool calls all failed with validation errors.
 */
function isPlanResponse(content: string, toolCalls?: any[]): boolean {
  // Check if tool calls had validation errors - these should trigger retry
  if (hasValidationErrors(toolCalls)) {
    return true;
  }
  
  // If there were successful tool calls (no validation errors), it's not just a plan
  if (toolCalls && toolCalls.length > 0 && !hasValidationErrors(toolCalls)) {
    return false;
  }
  
  const contentLower = content.toLowerCase();
  
  // Russian plan indicators
  const ruPlanIndicators = [
    'план действий',
    'рекомендуемый план',
    'нужно было бы',
    'можно было бы',
    'для того чтобы',
    'вам нужно',
    'необходимо использовать',
    'следует использовать',
    'рекомендую использовать',
    'шаг 1',
    'шаг 2',
    'первый шаг',
    'второй шаг',
    'пока этого не сделано',
    'без дополнительных данных',
    'не могу предоставить точный',
    'если у вас есть возможность',
    'запросить эту информацию',
    'повторного вызова',
  ];
  
  // English plan indicators
  const enPlanIndicators = [
    'action plan',
    'recommended plan',
    'you would need',
    'you should use',
    'i recommend using',
    'step 1',
    'step 2',
    'first step',
    'second step',
    'to do this',
    'here\'s how',
    'without additional data',
    'i cannot provide',
    'if you have access',
    'request this information',
  ];
  
  // Check for numbered lists that describe steps (1. Use X, 2. Use Y)
  const hasNumberedPlan = /\d+[\.\)]\s*(использовать|use|call|запросить|получить|вызвать)/i.test(content);
  
  // Check for plan indicators
  const hasPlanIndicator = [...ruPlanIndicators, ...enPlanIndicators].some(
    indicator => contentLower.includes(indicator)
  );
  
  return hasNumberedPlan || hasPlanIndicator;
}

// Export for use in ChatInterface for auto-retry
export { hasValidationErrors, extractValidationSuggestion };

interface MessageItemProps {
  message: Message;
  previousUserMessage?: string;  // The user message this was responding to
  onExecutePlan?: (planContent: string, originalQuestion: string) => void;
  onSchedulePrompt?: (promptText: string, resultPreview: string) => void;
  onClearAndRetry?: (originalQuestion: string) => void;
}

// Text of a hast subtree. react-markdown passes the hast `node` to every
// renderer; a fence's text is read off it here (no hast-util-to-string in
// node_modules, and four lines do not justify a dependency).
function hastText(n: any): string {
  if (!n) return '';
  if (n.type === 'text') return String(n.value ?? '');
  return (n.children ?? []).map(hastText).join('');
}

// Stable ReactMarkdown components — defined once outside the component to prevent re-renders
const markdownComponents = {
  // Images: dashboard screenshots live behind chat auth — AuthImage fetches
  // backend paths with the Bearer token (plain <img> cannot). STORY-184.
  img({ src, alt }: any) {
    return <AuthImage src={src} alt={alt} />;
  },

  // INLINE vs BLOCK is decided by the wrapper, not by a prop. react-markdown
  // 9 (this repo has never had another) passes no `inline` to `code`; a
  // fence is always `<pre><code>`, a backtick span is a bare `<code>`. Two
  // wrong discriminators preceded this one, each broken in one direction:
  //   - `!isInline && language` (…1.4.8-a): untagged fences became chips and
  //     the CPU bar chart collapsed into one wrapping line (STORY-273);
  //   - `!isInline` (1.4.8-b…1.5.0): `inline` is always undefined, so every
  //     span — tool names, `now-7d`, host ids — became a full-width CodeBlock
  //     with a copy button inside its list item (BUG-1.5.0_5).
  // So: `pre` owns the block (reads its <code> child off the hast node) and
  // `code` is only ever the chip. A fence's `code` child still renders the
  // chip first, but `pre` discards it, so nothing shows twice.
  pre({ node, children }: any) {
    const els = (node?.children ?? []).filter((c: any) => c.type === 'element');
    const c0 = els[0];
    // Markdown always yields exactly one <code> child here (mdast-util-to-hast),
    // so the guard only matters for raw HTML; a <pre> that is not a fence is
    // kept visible rather than swallowed.
    if (els.length === 1 && c0?.tagName === 'code') {
      const cls = (c0.properties?.className ?? []).join(' ');
      const m = /language-(\w+)/.exec(cls);
      return (
        <CodeBlock
          language={m ? m[1] : ''}
          value={hastText(c0).replace(/\n$/, '')}
        />
      );
    }
    return <pre>{children}</pre>;
  },
  // `node` is destructured out so the hast element never reaches the DOM.
  code({ className: _className, children, node: _node, ...props }: any) {
    return (
      <code
        className="px-1.5 py-0.5 text-sm bg-gray-100 dark:bg-gray-800 rounded font-mono text-pink-600 dark:text-pink-400"
        {...props}
      >
        {children}
      </code>
    );
  },

  // Links with external icon
  a({ href, children, ...props }: any) {
    const isExternal = href?.startsWith('http');
    return (
      <a
        href={href}
        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline inline-flex items-center gap-1"
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
        {isExternal && <ExternalLink className="h-3 w-3 inline" />}
      </a>
    );
  },

  // Tables
  table({ children, ...props }: any) {
    return (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700 border border-gray-300 dark:border-gray-700" {...props}>
          {children}
        </table>
      </div>
    );
  },
  thead({ children, ...props }: any) {
    return (
      <thead className="bg-gray-100 dark:bg-gray-800" {...props}>
        {children}
      </thead>
    );
  },
  tbody({ children, ...props }: any) {
    return (
      <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900" {...props}>
        {children}
      </tbody>
    );
  },
  th({ children, ...props }: any) {
    return (
      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider" {...props}>
        {children}
      </th>
    );
  },
  td({ children, ...props }: any) {
    return (
      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300" {...props}>
        {children}
      </td>
    );
  },

  // Headings
  h1({ children, ...props }: any) {
    return <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900 dark:text-white" {...props}>{children}</h1>;
  },
  h2({ children, ...props }: any) {
    return <h2 className="text-xl font-bold mt-5 mb-3 text-gray-900 dark:text-white" {...props}>{children}</h2>;
  },
  h3({ children, ...props }: any) {
    return <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-white" {...props}>{children}</h3>;
  },
  h4({ children, ...props }: any) {
    return <h4 className="text-base font-semibold mt-3 mb-2 text-gray-900 dark:text-white" {...props}>{children}</h4>;
  },

  // Lists
  ul({ children, ...props }: any) {
    return <ul className="list-disc list-inside my-2 space-y-1 text-gray-800 dark:text-gray-200" {...props}>{children}</ul>;
  },
  ol({ children, ...props }: any) {
    return <ol className="list-decimal list-inside my-2 space-y-1 text-gray-800 dark:text-gray-200" {...props}>{children}</ol>;
  },
  li({ children, ...props }: any) {
    return <li className="ml-4" {...props}>{children}</li>;
  },

  // Blockquotes
  blockquote({ children, ...props }: any) {
    return (
      <blockquote className="border-l-4 border-blue-500 dark:border-blue-400 pl-4 py-2 my-3 italic bg-blue-50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-300" {...props}>
        {children}
      </blockquote>
    );
  },

  // Horizontal rule
  hr({ ...props }: any) {
    return <hr className="my-4 border-gray-300 dark:border-gray-700" {...props} />;
  },

  // Paragraphs
  p({ children, ...props }: any) {
    return <p className="my-2 leading-relaxed text-gray-800 dark:text-gray-200" {...props}>{children}</p>;
  },

  // Strong/Bold
  strong({ children, ...props }: any) {
    return <strong className="font-bold text-gray-900 dark:text-white" {...props}>{children}</strong>;
  },

  // Emphasis/Italic
  em({ children, ...props }: any) {
    return <em className="italic text-gray-800 dark:text-gray-200" {...props}>{children}</em>;
  },

  // Strikethrough (GFM)
  del({ children, ...props }: any) {
    return <del className="line-through text-gray-500 dark:text-gray-400" {...props}>{children}</del>;
  },

  // Task lists (GFM)
  input({ type, checked, ...props }: any) {
    if (type === 'checkbox') {
      return <input type="checkbox" checked={checked} disabled className="mr-2 rounded" {...props} />;
    }
    return <input type={type} {...props} />;
  },
};

const remarkPluginsStable = [remarkGfm];
const rehypePluginsStable = [rehypeRaw];
// A user's own message is shown literally: without rehype-raw any HTML it
// contains is escaped instead of rendered (F-5b). Assistant output keeps raw HTML.
const rehypePluginsPlain: typeof rehypePluginsStable = [];

/**
 * Shows a per-cluster status bar when the response came from multiple clusters.
 * Each cluster shows: name, success/fail indicator, and response time.
 */
function clusterModeLabel(mode: string, language: string): string {
  if (mode === 'parallel') return language === 'ru' ? 'параллельно' : 'parallel';
  return language === 'ru' ? 'последовательно' : 'sequential';
}

const ClusterResultsBanner: React.FC<{ mc: MultiClusterInfo; language: string }> = ({ mc, language }) => {
  const nOk = mc.clusters.filter(c => c.success).length;
  const nFail = mc.clusters.length - nOk;
  const { t } = useTranslation();
  const sec = language === 'ru' ? 'с' : 's';
  const modeLabel = clusterModeLabel(mc.cluster_routing_mode, language);

  return (
    <div className="mb-2 p-2 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20">
      <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 mb-1.5">
        <Globe className="w-3.5 h-3.5" />
        <span className="font-medium">
          {language === 'ru' ? 'Сбор метрик с нескольких кластеров' : 'Metrics from multiple clusters'}
        </span>
        <span className="text-blue-500 dark:text-blue-400">
          ({modeLabel}, {mc.total_duration_seconds.toFixed(1)}{sec})
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {mc.clusters.map(c => (
          <span
            key={c.id}
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium',
              c.success
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
            )}
          >
            <span className={clsx(
              'w-1.5 h-1.5 rounded-full',
              c.success ? 'bg-green-500' : 'bg-red-500',
            )} />
            {c.name}
            <span className="opacity-60">{c.duration_seconds.toFixed(1)}{sec}</span>
            {c.error && <span className="opacity-60 truncate max-w-[120px]">{c.error}</span>}
          </span>
        ))}
      </div>
      {nFail > 0 && (
        <p className="mt-1 text-[10px] text-red-600 dark:text-red-400">
          {formatCount(language, nFail, t.analytics.counts.failedClusters)}
        </p>
      )}
    </div>
  );
};

// Wrapped with React.memo to prevent unnecessary re-renders during polling
const MessageItemComponent: React.FC<MessageItemProps> = ({ message, previousUserMessage, onExecutePlan, onSchedulePrompt, onClearAndRetry }) => {
  const [copied, setCopied] = useState(false);
  const [expandedTools, setExpandedTools] = useState<Record<number, boolean>>({});
  // Use fine-grained selector — only re-render when showTimestamps actually changes
  const showTimestamps = useAppStore(s => s.settings.showTimestamps);
  const messageRef = useRef<HTMLDivElement>(null);
  // STORY-304: what an incident card's Print shows — its rendered text.
  const contentRef = useRef<HTMLDivElement>(null);
  const { t, language } = useTranslation();
  // STORY-298: the charts this answer carries that can be drawn. Only these
  // hide their text block; a malformed or absent chart leaves the text whole.
  const charts = useMemo(
    () => (message.type === 'assistant' ? drawableCharts(message.metadata?.chartData) : []),
    [message.type, message.metadata?.chartData],
  );

  // Tool calls start collapsed by default - user clicks to expand
  // No auto-expand behavior

  const handleCopy = async () => {
    try {
      await copyToClipboard(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t.chat.copiedToClipboard);
    } catch (error) {
      toast.error(t.chat.copyFailed);
    }
  };

  const getMessageIcon = () => {
    switch (message.type) {
      case 'user':
        return <User className="h-5 w-5" />;
      case 'assistant':
        return <Bot className="h-5 w-5" />;
      case 'system':
        return <AlertTriangle className="h-5 w-5" />;
      default:
        return <Bot className="h-5 w-5" />;
    }
  };

  const getMessageBgColor = () => {
    switch (message.type) {
      case 'user':
        return 'bg-blue-50 dark:bg-blue-900/20';
      case 'assistant':
        return 'bg-gray-50 dark:bg-gray-800';
      case 'system':
        return 'bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'bg-gray-50 dark:bg-gray-800';
    }
  };

  const getAvatarBgColor = () => {
    switch (message.type) {
      case 'user':
        return 'bg-blue-600 text-white';
      case 'assistant':
        return 'bg-green-600 text-white';
      case 'system':
        return 'bg-yellow-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  return (
    <div ref={messageRef} data-message-type={message.type} className={clsx('group relative rounded-lg p-4 transition-colors', getMessageBgColor())}>
      <div className="flex space-x-3">
        {/* Avatar */}
        <div className={clsx('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center', getAvatarBgColor())}>
          {getMessageIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {message.type === 'user' 
                ? (language === 'ru' ? 'Вы' : 'You') 
                : message.type === 'assistant' 
                ? (language === 'ru' ? 'Ассистент' : 'Assistant') 
                : (language === 'ru' ? 'Система' : 'System')}
            </span>
            {showTimestamps && message.timestamp && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {(() => {
                  try {
                    const date = message.timestamp instanceof Date 
                      ? message.timestamp 
                      : new Date(message.timestamp);
                    return isNaN(date.getTime()) ? '' : format(date, 'HH:mm');
                  } catch {
                    return '';
                  }
                })()}
              </span>
            )}
            {message.metadata?.model && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {message.metadata.model}
              </span>
            )}
            {message.metadata?.contextUsed && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                {language === 'ru' ? 'Контекст' : 'Context'}
              </span>
            )}
            {message.metadata?.clusterMention?.names?.length ? (
              <span
                data-testid="cluster-mention-chip"
                title={t.clusterMode?.mentionChipTitle}
                className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-full"
              >
                <Globe className="w-3 h-3" aria-hidden="true" />
                {t.clusterMode?.mentionChip || 'Cluster'}: {message.metadata.clusterMention.names.join(', ')}
              </span>
            ) : null}
            {message.metadata?.multiCluster?.multi_cluster && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full">
                <Globe className="w-3 h-3" />
                {message.metadata.multiCluster.clusters.length} {language === 'ru' ? 'кл.' : 'cl.'}
                {' '}({clusterModeLabel(message.metadata.multiCluster.cluster_routing_mode, language)})
              </span>
            )}
          </div>

          {/* STORY-520: AUTO mode moved off a failing model before this answer.
              "{model} answered" comes ONLY from stats.answered_by (answeredBy):
              without it no model answered, and the note says only who failed. */}
          {message.metadata?.failoverFrom?.length && t.chat?.failoverNote && t.chat?.failoverNoteNoAnswer ? (
            <p
              data-testid="failover-note"
              role="note"
              className="text-xs text-amber-700 dark:text-amber-300 mb-1"
            >
              {(message.metadata.answeredBy ? t.chat.failoverNote : t.chat.failoverNoteNoAnswer)
                .replace('{failed}', message.metadata.failoverFrom.map(providerDisplayName).join(', '))
                .replace('{model}', message.metadata.answeredBy ?? '')}
            </p>
          ) : null}

          {/* Multi-cluster source grouping banner */}
          {message.metadata?.multiCluster?.multi_cluster && (
            <ClusterResultsBanner mc={message.metadata.multiCluster} language={language} />
          )}

          {/* STORY-337: the table below shows the first rows itself, so the
              text preview of them (kept in the saved content for the CLI,
              the stream and the history) is not rendered a second time.
              STORY-298: likewise the text chart a drawn chart replaces —
              only for charts that are drawn (drawableCharts) and show all of
              it: a truncated chart (25 of 40 hosts) leaves its text block on
              screen, the only place the other hosts can still be read. */}
          <div ref={contentRef} data-message-content className="prose prose-sm max-w-none dark:prose-invert text-gray-900 dark:text-gray-100">
            <ReactMarkdown
              remarkPlugins={remarkPluginsStable}
              rehypePlugins={message.type === 'user' ? rehypePluginsPlain : rehypePluginsStable}
              components={markdownComponents}
            >
              {message.type === 'assistant'
                ? withoutPreviews(message.content, [message.metadata?.tableData, ...charts.filter((c) => !c.truncated)])
                : message.content}
            </ReactMarkdown>
          </div>

          {/* Structured table for large result sets (preferred) */}
          {message.type === 'assistant' && message.metadata?.tableData && (
            <DynamicTable tableData={message.metadata.tableData} />
          )}

          {/* STORY-298: the answer's charts, drawn interactively */}
          {charts.map((chart, i) => (
            <InteractiveChart key={i} chart={chart} />
          ))}

          {/* STORY-275: a proposed alert rule — saved only if the user confirms */}
          {message.type === 'assistant' && message.metadata?.alertRuleProposal && (
            <AlertRuleProposalCard proposal={message.metadata.alertRuleProposal} />
          )}

          {/* STORY-304: an incident card — download, print, send to Telegram */}
          {message.type === 'assistant' && message.metadata?.incidentCard && message.content.length > 0 && (
            <IncidentCardActions
              content={message.content}
              card={message.metadata.incidentCard}
              printTarget={contentRef}
            />
          )}

          {/* Fallback: pagination controls when no structured table data */}
          {message.type === 'assistant' && !message.metadata?.tableData && message.metadata?.pagination && (
            <PaginationControls
              pagination={message.metadata.pagination}
              originalContent={message.content}
            />
          )}

          {message.metadata?.error && (
            <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-400">
              <strong>Error:</strong> {message.metadata.error}
            </div>
          )}

          {/* Context overflow: offer to clear history and retry */}
          {message.type === 'assistant' && previousUserMessage && onClearAndRetry &&
           message.metadata?.error && /context too long|context too large/i.test(message.metadata.error) && (
            <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {language === 'ru'
                    ? 'Контекст разговора слишком длинный. Очистить историю и повторить запрос?'
                    : 'Conversation context is too long. Clear history and retry?'}
                </span>
                <button
                  onClick={() => onClearAndRetry(previousUserMessage)}
                  className="ml-3 flex-shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors"
                >
                  {language === 'ru' ? 'Очистить и повторить' : 'Clear & Retry'}
                </button>
              </div>
            </div>
          )}

          {/* Execute Plan button - shown when LLM explains instead of executing */}
          {message.type === 'assistant' && 
           previousUserMessage && 
           onExecutePlan && 
           isPlanResponse(message.content, message.metadata?.toolCalls) && (
            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {language === 'ru' 
                      ? 'Ассистент предложил план вместо выполнения' 
                      : 'Assistant suggested a plan instead of executing'}
                  </span>
                </div>
                <button
                  onClick={() => onExecutePlan(message.content, previousUserMessage)}
                  className="flex items-center space-x-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium text-sm shadow-sm"
                >
                  <PlayCircle className="h-4 w-4" />
                  <span>{language === 'ru' ? 'Выполнить этот план' : 'Execute this plan'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Schedule Prompt button - shown on assistant messages that have a corresponding user prompt */}
          {message.type === 'assistant' &&
           previousUserMessage &&
           onSchedulePrompt &&
           !message.metadata?.isAutoRetry &&
           !message.metadata?.error &&
           message.content.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => onSchedulePrompt(previousUserMessage, message.content)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg border border-transparent hover:border-blue-200 dark:hover:border-blue-800 transition-all opacity-0 group-hover:opacity-100"
                title={language === 'ru' ? 'Запланировать этот запрос' : 'Schedule this prompt'}
              >
                <CalendarClock className="h-3.5 w-3.5" />
                <span>{language === 'ru' ? 'Запланировать' : 'Schedule'}</span>
              </button>
            </div>
          )}

          {message.metadata?.toolCalls && message.metadata.toolCalls.length > 0 && (() => {
            // Deduplicate tool calls to avoid showing the same call multiple times
            const callMap = new Map<string, { call: any; count: number; originalIndex: number }>();
            message.metadata.toolCalls.forEach((call: any, idx: number) => {
              const argsStr = JSON.stringify(call.args || {}, Object.keys(call.args || {}).sort());
              const signature = `${toolCallName(call)}:${argsStr}`;
              
              if (callMap.has(signature)) {
                callMap.get(signature)!.count++;
              } else {
                callMap.set(signature, { call, count: 1, originalIndex: idx });
              }
            });
            
            const deduplicatedCalls = Array.from(callMap.values());
            
            return (
            <div className="mt-3 p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/60 dark:to-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <div className="h-1 w-1 rounded-full bg-blue-500 dark:bg-blue-400"></div>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Tools Used</span>
                  {deduplicatedCalls.length < message.metadata.toolCalls.length && (
                    <span className="text-[10px] text-orange-600 dark:text-orange-400 font-medium">
                      ({formatCount(language, message.metadata.toolCalls.length - deduplicatedCalls.length, t.analytics.counts.hiddenDuplicates)})
                    </span>
                  )}
              </div>
              <ul className="space-y-2">
                  {deduplicatedCalls.map((item, dedupIdx) => {
                    const tc = item.call;
                    const idx = item.originalIndex;
                    const isDuplicate = item.count > 1;
                  const hasArgs = !!tc.args && Object.keys(tc.args).length > 0;
                  const hasOutput = !!tc.output;
                  const isOpen = !!expandedTools[idx];
                  const preview = hasArgs
                    ? Object.entries(tc.args)
                        .slice(0, 2)
                        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v).slice(0, 30) : String(v).slice(0, 30)}`)
                        .join(', ')
                    : '';
                  return (
                      <li key={dedupIdx} className="text-xs bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="p-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-w-0 flex-1">
                            {(hasArgs || hasOutput) ? (
                              <button
                                onClick={() => setExpandedTools((s) => ({ ...s, [idx]: !isOpen }))}
                                className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0 transition-colors"
                                title={isOpen ? t.chat.hideDetails : t.chat.showDetails}
                                aria-label={isOpen ? t.chat.hideDetails : t.chat.showDetails}
                                aria-expanded={isOpen}
                              >
                                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                            ) : (
                              <span className="inline-block w-4 mr-2 flex-shrink-0" />
                            )}
                            <span className="font-semibold text-blue-600 dark:text-blue-400 font-mono truncate">{toolCallName(tc)}</span>
                            {typeof tc.duration_ms !== 'undefined' && tc.duration_ms !== null && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-[10px] font-medium text-blue-700 dark:text-blue-300 flex-shrink-0">
                                {tc.duration_ms}ms
                              </span>
                            )}
                            {tc.status && (
                              <span className={clsx(
                                'ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0',
                                tc.status === 'ok' && 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
                                tc.status === 'error' && 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
                                tc.status === 'pending' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                              )}>
                                {tc.status}
                              </span>
                            )}
                              {isDuplicate && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 text-[10px] font-semibold flex-shrink-0" title={t.chat.calledTimes.replace('{count}', formatCount(language, item.count, t.analytics.counts.times))}>
                                  ×{item.count}
                                </span>
                              )}
                          </div>
                        </div>
                        {hasArgs && !isOpen && (
                          <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400 truncate pl-6">
                            {preview}{Object.keys(tc.args).length > 2 ? '...' : ''}
                          </div>
                        )}
                      </div>

                      {isOpen && hasArgs && (
                        <div className="px-2.5 pb-2.5">
                          <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{t.chat.arguments}</span>
                              <button
                                onClick={async () => {
                                  try { await copyToClipboard(JSON.stringify(tc.args, null, 2)); toast.success(t.chat.copiedToClipboard); } catch { /* noop */ }
                                }}
                                className="text-[11px] px-2 py-1 rounded bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors font-medium"
                                title={t.chat.copyJson}
                              >
                                {t.chat.copy}
                              </button>
                            </div>
                            <pre className="text-[11px] font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words overflow-x-auto leading-relaxed">{JSON.stringify(tc.args, null, 2)}</pre>
                          </div>
                        </div>
                      )}

                      {isOpen && hasOutput && (
                        <div className="px-2.5 pb-2.5">
                          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide">{t.chat.output}</span>
                              <button
                                onClick={async () => {
                                  try { await copyToClipboard(tc.output as string); toast.success(t.chat.copiedToClipboard); } catch { /* noop */ }
                                }}
                                className="text-[11px] px-2 py-1 rounded bg-green-200 dark:bg-green-900/40 text-green-800 dark:text-green-200 hover:bg-green-300 dark:hover:bg-green-800/40 transition-colors font-medium"
                                title={t.chat.copyOutput}
                              >
                                {t.chat.copy}
                              </button>
                            </div>
                            <pre className="text-[11px] font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words overflow-x-auto leading-relaxed max-h-64 overflow-y-auto">{tc.output}</pre>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })()}

          {message.metadata?.toolCalls && message.metadata.toolCalls.length > 0 && (
            <ToolTimeline calls={message.metadata.toolCalls as any} />
          )}

          {message.metadata?.tokens && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t.chat.tokens}: {message.metadata.tokens}
            </div>
          )}
        </div>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity"
          title={t.chat.copyMessage}
          aria-label={t.chat.copyMessage}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
};

// Export memoized component - only re-renders when props change, not on parent re-renders
export const MessageItem = React.memo(MessageItemComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if message content/metadata changes
  // Deep comparison for metadata to catch nested changes
  const metadataEqual = JSON.stringify(prevProps.message.metadata) === JSON.stringify(nextProps.message.metadata);
  
  // Safely compare timestamps (handle Date objects, strings, numbers, or undefined)
  const getTimestamp = (ts: Date | string | number | undefined | null): number => {
    if (!ts) return 0;
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'number') return ts;
    if (typeof ts === 'string') return new Date(ts).getTime() || 0;
    return 0;
  };
  
  const prevTimestamp = getTimestamp(prevProps.message.timestamp);
  const nextTimestamp = getTimestamp(nextProps.message.timestamp);
  
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.type === nextProps.message.type &&
    prevProps.message.content === nextProps.message.content &&
    prevTimestamp === nextTimestamp &&
    metadataEqual &&
    prevProps.previousUserMessage === nextProps.previousUserMessage &&
    prevProps.onExecutePlan === nextProps.onExecutePlan &&
    prevProps.onSchedulePrompt === nextProps.onSchedulePrompt &&
    prevProps.onClearAndRetry === nextProps.onClearAndRetry
  );
});